import { base64ToBytes } from '../net/base64';

/**
 * Plays every other peer's live microphone capture as it arrives — player-to-player
 * voice (plan/ai-in-room.md): the people in a room hear each other, not only the
 * assistant. This is "the hard part" that document calls out by name, and the reason is
 * specific: capture already arrives in small, sequential, *not independently decodable*
 * chunks of whatever container `MicrophoneCapture` picked (`audio/webm;codecs=opus` on
 * Chromium, and therefore on Quest Browser), and the only other playback path this
 * project has — `AssistantController.playNext`'s `new Audio(data:...;base64,...)` — needs
 * a *complete* clip up front. Waiting for a peer's stream to finish before playing any of
 * it would mean hearing them a beat after they stopped talking — exactly the delayed-echo
 * problem `MicrophoneCapture`'s own chunking exists to avoid, just moved from the sender's
 * side to the receiver's.
 *
 * The fix is MediaSource Extensions: one `MediaSource` + `SourceBuffer` pair per speaking
 * peer, fed chunks as they arrive rather than assembled first. One pair *per peer*,
 * deliberately, not one shared pair for the room — two people talking at once must not
 * interleave into a single stream, and a `SourceBuffer` has no concept of "whose bytes
 * these are" to keep them apart if they did.
 *
 * **Never plays this browser's own voice back.** Structurally redundant with the server's
 * own broadcast — `RoomSyncHub.StreamVoiceChunk`/`StartVoiceStream`/`EndVoiceStream` all
 * notify `OthersInGroup`, never the caller, so an event carrying this client's own peerId
 * should never even arrive — but asserted here too, by name, because plan/ai-in-room.md
 * calls it out as a real requirement rather than an implementation detail, and a future
 * change to the broadcast scope should not silently reintroduce it.
 *
 * **Codec support is checked, not assumed.** `MediaSource.isTypeSupported` gates every
 * stream before a `MediaSource`/`SourceBuffer` pair is ever created for it — historically
 * awkward on Quest's browser, per this feature's own brief, so a peer whose declared
 * codec is not supported here is skipped outright (chunks silently dropped, `onUnsupported`
 * fired once per distinct codec string) rather than assumed to work and left to fail
 * loudly mid-stream.
 *
 * No Scene or Mesh dependency and no spatial audio, on purpose, the same separation
 * `AssistantController` already keeps: this class owns plain `HTMLAudioElement`s, a
 * caller in main.ts wires it to the hub's `voiceStreamStarted`/`voiceChunk`/
 * `voiceStreamEnded` events and to `peerLeft` for disconnect cleanup. plan/ai-in-room.md
 * is explicit that this is room broadcast, not positioned sound — there is nothing here
 * that could place a voice in space even if a future change wanted to.
 */

/** The browser surface this class needs, factored out so tests can supply fakes — jsdom
 *  has no MediaSource implementation, the same constraint `PlaybackAmplitude`/`VoiceBands`
 *  already document for `AudioContext`. */
export interface VoicePlaybackDeps {
  /** `typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(mimeType)` in the
   *  real implementation — a browser with no MediaSource at all is "unsupported", not a
   *  crash. */
  isTypeSupported: (mimeType: string) => boolean;
  createMediaSource: () => MediaSource;
  createAudio: () => HTMLAudioElement;
  createObjectUrl: (source: MediaSource) => string;
  revokeObjectUrl: (url: string) => void;
}

const REAL_DEPS: VoicePlaybackDeps = {
  isTypeSupported: (mimeType) =>
    typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(mimeType),
  createMediaSource: () => new MediaSource(),
  createAudio: () => new Audio(),
  createObjectUrl: (source) => URL.createObjectURL(source),
  revokeObjectUrl: (url) => URL.revokeObjectURL(url),
};

interface PeerStream {
  mediaSource: MediaSource;
  audio: HTMLAudioElement;
  sourceBuffer: SourceBuffer | null;
  /** Chunks that arrived before the SourceBuffer was ready, or while a previous append
   *  was still `updating` — MSE allows exactly one `appendBuffer` in flight at a time. */
  readonly queue: Uint8Array[];
  /** True once `end()` has been called: no more chunks are coming, so once `queue`
   *  drains, `mediaSource.endOfStream()` should run instead of waiting for more. */
  ending: boolean;
  readonly objectUrl: string;
  /** Guards `teardown` against running twice for the same entry. */
  torn: boolean;
}

// spec: SPEC-112
export class PeerVoicePlayer {
  private readonly peers = new Map<string, PeerStream>();
  private readonly reportedUnsupported = new Set<string>();

  /** Fired at most once per distinct unsupported codec string, so a caller can surface it
   *  without spamming a notice per peer or per chunk. */
  onUnsupported: ((mimeType: string) => void) | null = null;

  constructor(
    private readonly selfPeerId: () => string | null,
    private readonly deps: VoicePlaybackDeps = REAL_DEPS,
  ) {}

  /**
   * A peer's microphone just went live. Tears down any previous stream already tracked
   * for this peer first — a dangling one that never got a matching `end()` (a disconnect
   * with no `peerLeft` yet processed, or the rapid-toggle race `AssistantController`'s own
   * `syncCaptureHardware` can in principle hit) loses to a fresh Start rather than leaking
   * or fighting it for the same `<audio>` element.
   */
  start(peerId: string, mimeType: string): void {
    if (this.isSelf(peerId)) {
      return;
    }

    this.teardown(peerId);

    if (!this.deps.isTypeSupported(mimeType)) {
      if (!this.reportedUnsupported.has(mimeType)) {
        this.reportedUnsupported.add(mimeType);
        this.onUnsupported?.(mimeType);
      }

      return;
    }

    const mediaSource = this.deps.createMediaSource();
    const audio = this.deps.createAudio();
    const objectUrl = this.deps.createObjectUrl(mediaSource);
    audio.src = objectUrl;

    const entry: PeerStream = {
      mediaSource,
      audio,
      sourceBuffer: null,
      queue: [],
      ending: false,
      objectUrl,
      torn: false,
    };

    this.peers.set(peerId, entry);

    mediaSource.addEventListener(
      'sourceopen',
      () => {
        // The peer's own stream could already have been superseded or torn down while
        // this was waiting for the browser to actually open the source.
        if (entry.torn) {
          return;
        }

        try {
          const sourceBuffer = mediaSource.addSourceBuffer(mimeType);
          sourceBuffer.addEventListener('updateend', () => this.pump(peerId));
          entry.sourceBuffer = sourceBuffer;
          this.pump(peerId);
        } catch (error) {
          console.warn('Voice: failed to open a playback buffer for a peer.', error);
          this.teardown(peerId);
        }
      },
      { once: true },
    );

    const onSettled = () => this.teardown(peerId);
    audio.addEventListener('ended', onSettled, { once: true });
    audio.addEventListener('error', onSettled, { once: true });

    void audio.play().catch((error: unknown) => {
      console.warn('Voice: failed to start playing a peer’s voice.', error);
    });
  }

  /** One chunk of `peerId`'s current stream, base64-encoded exactly as it arrived on the
   *  wire (`VoiceChunkUpdate.data`). Silently dropped if this peer has no open stream —
   *  never started, already ended, or an unsupported codec — the same tolerance every
   *  other streamed message on this hub already has for arriving after its own context
   *  is gone. */
  appendChunk(peerId: string, base64: string): void {
    if (this.isSelf(peerId)) {
      return;
    }

    const entry = this.peers.get(peerId);
    if (!entry || entry.ending) {
      return;
    }

    entry.queue.push(base64ToBytes(base64));
    this.pump(peerId);
  }

  /**
   * The matching close for `start()`. Marks the stream as finished and lets whatever is
   * still queued drain before calling `mediaSource.endOfStream()` — never discards
   * already-arrived audio just because the peer stopped talking a moment before the last
   * chunk was appended. A no-op for a peer with no tracked stream, the same tolerance the
   * server-side `EndVoiceStream` has for a stray call.
   */
  end(peerId: string): void {
    const entry = this.peers.get(peerId);
    if (!entry) {
      return;
    }

    entry.ending = true;
    this.pump(peerId);
  }

  /** For a caller (main.ts, tests) that wants to know whether a peer is currently being
   *  tracked at all — never used for correctness inside this class itself. */
  isActive(peerId: string): boolean {
    return this.peers.has(peerId);
  }

  /**
   * The hard teardown for a peer who is *gone*, not merely quiet — `main.ts`'s own
   * `peerLeft` handler, not `end()`. The two differ: `end()` marks a stream finished and
   * lets whatever is already queued keep draining until the browser's own `ended`/`error`
   * event closes it out, which is right for a peer who just stopped talking but is still in
   * the room. It is not enough for a peer who has actually left, because that event is not
   * guaranteed to ever arrive — `start()`'s own catch around `audio.play()` already
   * documents that a browser can refuse playback outright (the autoplay-policy hazard
   * CLAUDE.md's `AssistantSpeechPlayer` entry describes at length for the assistant's own
   * clips), and a refused `play()` never fires `ended` or `error` on that element. Without
   * this, that peer's `MediaSource`, `<audio>` element and object URL stay alive for the
   * rest of the session — `isActive()` keeps reporting `true` forever, and
   * `getAudioElement()` keeps handing `VoiceBands` a dead element — because nothing had any
   * other way to reach `teardown()` for that peerId again. `isSelf` guarded for the same
   * reason `start()`/`appendChunk()` already are, even though nothing today can call this
   * with this browser's own peerId (`peerLeft` never fires for yourself): consistent with
   * the rest of this class's own convention, not because anything currently depends on it.
   */
  remove(peerId: string): void {
    if (this.isSelf(peerId)) {
      return;
    }

    this.teardown(peerId);
  }

  /**
   * The live `<audio>` element behind a peer's current stream, if any — for a caller
   * (`AvatarManager.voiceStreamStarted`, via `main.ts`) that wants to tap the same playback
   * graph for a purpose this class has nothing to do with (the visor voice visualisation,
   * `voice/VoiceBands.ts`). Never used for correctness inside this class itself, the same as
   * `isActive`. `start()` builds a fresh `<audio>` element on every call, so a caller re-reading
   * this after each `voiceStreamStarted` event always gets the element that actually belongs to
   * the stream currently in flight, never a stale one left over from an earlier session.
   */
  getAudioElement(peerId: string): HTMLAudioElement | undefined {
    return this.peers.get(peerId)?.audio;
  }

  /** Stops and discards every tracked stream immediately, for page teardown — the same
   *  "drop anything held" shape `AssistantController.releaseImmediately` already uses. */
  releaseAll(): void {
    for (const peerId of [...this.peers.keys()]) {
      this.teardown(peerId);
    }
  }

  private isSelf(peerId: string): boolean {
    return peerId !== '' && peerId === this.selfPeerId();
  }

  /**
   * The one place a chunk is actually appended, or a finished stream is actually closed.
   * MSE allows exactly one `appendBuffer` in flight per `SourceBuffer`; this is called
   * again from that append's own `updateend`, so it drains the queue one chunk at a time
   * and — once the queue is empty and `ending` — calls `endOfStream()` exactly once.
   */
  private pump(peerId: string): void {
    const entry = this.peers.get(peerId);
    if (!entry || !entry.sourceBuffer || entry.sourceBuffer.updating) {
      return;
    }

    const next = entry.queue.shift();

    if (next) {
      try {
        // TypeScript's lib.dom.d.ts types SourceBuffer.appendBuffer against a Uint8Array
        // backed specifically by ArrayBuffer, not the broader ArrayBufferLike a plain
        // `new Uint8Array(n)` infers as since TS 5.7 — a real browser accepts any typed
        // array view regardless, so this is a type-system mismatch, not a runtime one.
        entry.sourceBuffer.appendBuffer(next as BufferSource);
      } catch (error) {
        console.warn('Voice: failed to append a chunk of a peer’s voice.', error);
      }

      return;
    }

    if (!entry.ending) {
      return;
    }

    if (entry.mediaSource.readyState === 'open') {
      try {
        entry.mediaSource.endOfStream();
      } catch {
        // Already ended or errored under us (a browser quirk, or a second call racing
        // the first) — nothing further to do either way.
      }
    }
  }

  private teardown(peerId: string): void {
    const entry = this.peers.get(peerId);
    if (!entry || entry.torn) {
      return;
    }

    entry.torn = true;
    entry.audio.pause();
    this.deps.revokeObjectUrl(entry.objectUrl);
    this.peers.delete(peerId);
  }
}
